import type { Child, ChildAboutInput, ConditionKey, DiagnosisStatus, SchoolSetting, TherapyKey } from '@/api/types';

export const STATUSES: DiagnosisStatus[] = ['none', 'waiting', 'diagnosed', 'unsure'];
export const CONDITIONS: ConditionKey[] = [
  'autism',
  'adhd',
  'speech',
  'learning',
  'developmental_delay',
  'intellectual_disability',
  'sensory',
  'coordination',
  'anxiety',
  'down_syndrome',
  'cerebral_palsy',
  'other',
];
export const THERAPIES: TherapyKey[] = ['speech', 'occupational', 'behavioural', 'special_education', 'physiotherapy', 'psychology', 'other'];
export const SCHOOLS: SchoolSetting[] = ['not_yet', 'mainstream', 'inclusive', 'special', 'home'];

/**
 * Class choices, stored as the English value (what staff and reports read) and shown in the
 * parent's language. Indian school years: pre-primary, then Class 1–12.
 */
export const GRADES: Array<{ value: string; key: string; n?: number }> = [
  { value: 'Playgroup', key: 'about.grade.playgroup' },
  { value: 'Nursery', key: 'about.grade.nursery' },
  { value: 'LKG', key: 'about.grade.lkg' },
  { value: 'UKG', key: 'about.grade.ukg' },
  ...Array.from({ length: 12 }, (_, i) => ({ value: `Class ${i + 1}`, key: 'about.grade.class', n: i + 1 })),
];

/** What the parent has picked or typed in the "about my child" questions; every part is optional. */
export interface AboutState {
  status?: DiagnosisStatus;
  conditions: ConditionKey[];
  other: string;
  therapies: TherapyKey[];
  school?: SchoolSetting;
  grade: string;
  languages: string;
  loves: string;
  upsets: string;
}

export const emptyAbout = (): AboutState => ({
  conditions: [],
  other: '',
  therapies: [],
  grade: '',
  languages: '',
  loves: '',
  upsets: '',
});

export const aboutStateOf = (child?: Child): AboutState => {
  const a = child?.about;
  return {
    status: a?.diagnosis?.status,
    conditions: a?.diagnosis?.conditions ?? [],
    other: a?.diagnosis?.other ?? '',
    therapies: a?.therapies ?? [],
    school: a?.school?.setting,
    grade: a?.school?.grade ?? '',
    languages: (a?.languages ?? []).join(', '),
    loves: a?.loves ?? '',
    upsets: a?.upsets ?? '',
  };
};

export const asksConditions = (status?: DiagnosisStatus) => status === 'diagnosed' || status === 'waiting';

export const toggle = <T,>(list: T[], item: T) => (list.includes(item) ? list.filter((x) => x !== item) : [...list, item]);

/** Everything optional, blanks dropped, languages split on commas (Latin or Arabic) and capped at six. */
export const buildAbout = (s: AboutState): ChildAboutInput => {
  const withConditions = asksConditions(s.status);
  return {
    diagnosis: s.status
      ? { status: s.status, conditions: withConditions ? s.conditions : [], other: withConditions && s.conditions.includes('other') ? s.other.trim() || undefined : undefined }
      : undefined,
    therapies: s.therapies,
    school: s.school || s.grade.trim() ? { setting: s.school, grade: s.grade.trim() || undefined } : undefined,
    languages: s.languages
      .split(/[,،]/)
      .map((l) => l.trim())
      .filter(Boolean)
      .slice(0, 6),
    loves: s.loves.trim() || undefined,
    upsets: s.upsets.trim() || undefined,
  };
};

/**
 * Profile completion: one step per question the parent is being asked right now. Conditions count
 * only once a diagnosis (or a wait for one) is picked, and the class only for a child in school.
 */
export const profileSteps = (s: AboutState): { done: number; total: number } => {
  const steps: boolean[] = [
    !!s.status,
    s.therapies.length > 0,
    !!s.school,
    s.languages.split(/[,،]/).some((l) => l.trim()),
    !!s.loves.trim(),
    !!s.upsets.trim(),
  ];
  if (asksConditions(s.status)) steps.push(s.conditions.length > 0);
  if (s.school && s.school !== 'not_yet') steps.push(!!s.grade.trim());
  return { done: steps.filter(Boolean).length, total: steps.length };
};

/** True once the parent has answered anything, so a create call sends `about` only when there is something to keep. */
export const hasAboutAnswers = (s: AboutState): boolean =>
  !!(s.status || s.therapies.length || s.school || s.grade.trim() || s.languages.split(/[,،]/).some((l) => l.trim()) || s.loves.trim() || s.upsets.trim());
